import { useState } from "react";

import {
  hsvaToHSLA,
  hsvaToRGBA,
  hslaToHSVA,
  rgbaToHSVA,
  type HSVA,
} from "@excalidraw/common";

import DropdownMenu from "../dropdownMenu/DropdownMenu";
import { chevronRight } from "../icons";

type Format = "rgb" | "hsl" | "hsb";

const FORMAT_OPTIONS: { value: Format; label: string }[] = [
  { value: "rgb", label: "RGB" },
  { value: "hsl", label: "HSL" },
  { value: "hsb", label: "HSB" },
];

const NumberField = ({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) => (
  <label className="spectrum__field">
    <span>{label}</span>
    <input
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={(e) => {
        const n = parseFloat(e.target.value);
        if (!Number.isNaN(n)) {
          onChange(Math.min(max, Math.max(min, n)));
        }
      }}
      onKeyDown={(e) => e.stopPropagation()}
    />
  </label>
);

export const ColorFormatFields = ({
  hsva,
  onChange,
}: {
  hsva: HSVA;
  onChange: (hsva: HSVA) => void;
}) => {
  const [format, setFormat] = useState<Format>("rgb");
  const [formatMenuOpen, setFormatMenuOpen] = useState(false);

  const fields = (() => {
    if (format === "rgb") {
      const rgba = hsvaToRGBA(hsva);
      return (["r", "g", "b"] as const).map((ch) => (
        <NumberField
          key={ch}
          label={ch.toUpperCase()}
          value={Math.round(rgba[ch])}
          min={0}
          max={255}
          onChange={(v) => onChange(rgbaToHSVA({ ...rgba, [ch]: v }))}
        />
      ));
    }
    if (format === "hsl") {
      const hsla = hsvaToHSLA(hsva);
      return (["h", "s", "l"] as const).map((ch) => (
        <NumberField
          key={ch}
          label={ch.toUpperCase()}
          value={Math.round(hsla[ch])}
          min={0}
          max={ch === "h" ? 360 : 100}
          onChange={(v) => onChange(hslaToHSVA({ ...hsla, [ch]: v }))}
        />
      ));
    }
    // hsb (a.k.a. hsv)
    return (["h", "s", "v"] as const).map((ch) => (
      <NumberField
        key={ch}
        label={ch === "v" ? "B" : ch.toUpperCase()}
        value={Math.round(hsva[ch])}
        min={0}
        max={ch === "h" ? 360 : 100}
        onChange={(v) => onChange({ ...hsva, [ch]: v })}
      />
    ));
  })();

  return (
    <div className="spectrum__format">
      <DropdownMenu open={formatMenuOpen}>
        <DropdownMenu.Trigger
          className="spectrum__format-select"
          aria-label="Color format"
          onToggle={() => setFormatMenuOpen((open) => !open)}
        >
          <span>{format.toUpperCase()}</span>
          <span className="spectrum__format-select-caret">{chevronRight}</span>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          className="spectrum__format-menu"
          onClickOutside={() => setFormatMenuOpen(false)}
          onSelect={() => setFormatMenuOpen(false)}
        >
          {FORMAT_OPTIONS.map((opt) => (
            <DropdownMenu.Item
              key={opt.value}
              value={opt.value}
              selected={format === opt.value}
              onSelect={() => setFormat(opt.value)}
            >
              {opt.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu>
      <div className="spectrum__fields">{fields}</div>
    </div>
  );
};
